import {SOCIAL_MEDIA_PUBLISHING_PAGE_HASHES} from '@Libraries/my-stuff/my-stuff-library';
import type {VoidFunction} from '@Utils/general.util';

/**
 * Every page-level My Stuff banner that participates in the single-banner rule. Mirrored in
 * MyStuffBannerId.php and legacy my-stuff.js (MY_STUFF_BANNER_IDS) — keep the values identical.
 */
export enum MyStuffBannerId {
  SOCIAL_ACCOUNTS_ATTENTION = 'social-accounts-attention',
  ACCOUNT_NOTICE = 'account-notice',
  DISCOUNT = 'discount',
  TICKETING_INTRO = 'ticketing-intro',
  REVIEW_EMAIL_CAMPAIGN = 'review-email-campaign',
  REVIEW_GENERIC = 'review-generic',
  REVIEW_EVENT = 'review-event',
  CUSTOMER_SUCCESS = 'customer-success',
  CUSTOMER_FEEDBACK_SURVEY = 'customer-feedback-survey',
}

/** The first id with an eligible candidate wins the visible banner slot. */
export const MY_STUFF_BANNER_PRIORITY: readonly MyStuffBannerId[] = [
  MyStuffBannerId.SOCIAL_ACCOUNTS_ATTENTION,
  MyStuffBannerId.ACCOUNT_NOTICE,
  MyStuffBannerId.DISCOUNT,
  MyStuffBannerId.TICKETING_INTRO,
  MyStuffBannerId.REVIEW_EMAIL_CAMPAIGN,
  MyStuffBannerId.REVIEW_GENERIC,
  MyStuffBannerId.REVIEW_EVENT,
  MyStuffBannerId.CUSTOMER_SUCCESS,
  MyStuffBannerId.CUSTOMER_FEEDBACK_SURVEY,
];

/**
 * Page hashes where each hook-registered banner may win the slot. Every banner registered via
 * useMyStuffBannerSlot MUST have an entry (the shell keeps hidden tabs' React roots mounted, so
 * eligibility alone would hold the slot from a left tab); legacy-reported banners need none.
 */
export const MY_STUFF_BANNER_HOST_PAGE_HASHES: Partial<Record<MyStuffBannerId, readonly string[]>> = {
  [MyStuffBannerId.SOCIAL_ACCOUNTS_ATTENTION]: SOCIAL_MEDIA_PUBLISHING_PAGE_HASHES,
};

/** Functional banners exempt from the one-marketing-banner-per-load lock. */
export const LOAD_LOCK_EXEMPT_MY_STUFF_BANNER_IDS: readonly MyStuffBannerId[] = [
  MyStuffBannerId.SOCIAL_ACCOUNTS_ATTENTION,
  MyStuffBannerId.ACCOUNT_NOTICE,
];

/** Registrant key for banners reported by legacy my-stuff.js; React banners use per-instance keys. */
export const LEGACY_MY_STUFF_BANNER_REGISTRANT = 'legacy';

export type LegacyMyStuffBannerCandidates = Partial<Record<MyStuffBannerId, boolean>>;

/** The window.PMW.myStuffBanners API consumed by legacy my-stuff.js. */
export interface MyStuffBannersBridge {
  onPageChanged: (legacyCandidates: LegacyMyStuffBannerCandidates) => void;
  setCandidate: (bannerId: MyStuffBannerId, isEligible: boolean) => void;
  notifyDismissed: (bannerId: MyStuffBannerId) => void;
  getActiveBannerId: () => MyStuffBannerId | null;
  subscribe: (onChange: (activeBannerId: MyStuffBannerId | null) => void) => VoidFunction;
}
